import React, { FC, useRef, useState } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import Slider, { Settings } from 'react-slick';
import styled from 'styled-components';
import Image from 'next/legacy/image';

const StyledWrapper = styled.div`
  width: 100%;

  .slider-wrapper {
    .slick-list {
      padding-top: 40px !important;
      padding-bottom: 40px !important;
    }

    .slick-next::before,
    .slick-prev::before {
      display: none !important;
    }
  }

  .control-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
  }
`;

const StyledImageWrapper = styled.div<{ width: number; height: number }>`
  display: flex !important;
  justify-content: center !important;

  .image-wrapper-container {
    width: 100%;
    max-width: ${(props) => (props.width ? `${props.width}px` : '100%')};
  }

  .image-wrapper-item {
    position: relative;
    padding-bottom: ${(props) => (props.width && props.height ? `calc(${props.height / props.width} * 100%)` : '50%')};
  }
`;

const StyledArrow = styled.div<{ isEnable?: boolean | undefined }>`
  display: block;
  width: 32px !important;
  height: 32px !important;
  text-align: center;
  cursor: ${(props) => (props.isEnable ? 'pointer' : 'not-allowed')};

  svg {
    background: ${(props) => (props.isEnable ? props.theme.color.primaryBase : '#A8A7A7')};
    color: ${(props) => props.theme.color.background};
    font-size: 32px;
    border-radius: 8px;
    padding: 8px;
  }
`;

interface ArrowProps {
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<any>;
  isEnable?: boolean;
}

type InspectionPointImage = {
  key: string;
  width: number;
  height: number;
  url: string;
  alt: string;
  className?: string;
  isOnlyDesktop?: boolean;
  isOnlyMobile?: boolean;
};

interface CCPO160PointImagesProps {
  images: InspectionPointImage[];
}

const PrevArrow: FC<ArrowProps> = ({ className, style, onClick, isEnable }) => (
  <StyledArrow isEnable={isEnable} className={`${className} slider-arrows`} style={style} onClick={onClick}>
    <LeftOutlined />
  </StyledArrow>
);

const NextArrow: FC<ArrowProps> = ({ className, style, onClick, isEnable }) => (
  <StyledArrow isEnable={isEnable} className={`${className} slider-arrows`} style={style} onClick={onClick}>
    <RightOutlined />
  </StyledArrow>
);

const CCPO160PointImages: FC<CCPO160PointImagesProps> = ({ images }) => {
  const sliderRef = useRef<Slider | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  const settings: Settings = {
    infinite: false,
    dots: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (oldIndex, newIndex) => {
      setActiveIndex(newIndex);
    },
  };

  return (
    <StyledWrapper>
      <div className="slider-wrapper">
        <Slider ref={sliderRef} {...settings}>
          {images.map((image: InspectionPointImage, index) => (
            <StyledImageWrapper key={image.key} width={image.width} height={image.height}>
              <div className="image-wrapper-container">
                <div className="image-wrapper-item">
                  <Image
                    alt={image.alt}
                    src={image.url.replace(
                      'https://cdn.carro.co/',
                      'https://cdn.carro.co/tr:n-carro_certified_page_inspection_points_image/',
                    )}
                    layout="fill"
                    sizes="(max-width: 768px) 100vw, 784px"
                  />
                </div>
              </div>
            </StyledImageWrapper>
          ))}
        </Slider>
        <div className="control-btn">
          <PrevArrow isEnable={activeIndex !== 0} onClick={() => sliderRef.current?.slickPrev()} />
          <NextArrow isEnable={activeIndex !== images.length - 1} onClick={() => sliderRef.current?.slickNext()} />
        </div>
      </div>
    </StyledWrapper>
  );
};

export default CCPO160PointImages;
